/**
 * Load Data Pipeline → a writer.
 *
 * One page per writer (W-DAT Observer, W-Sylectus Email), each showing what THAT writer REFUSED, and why. A refusal has no row in the intel database
 * by definition, so this reads the quarantine store (Firestore) alongside the
 * volume that made it through.
 *
 * An honest limitation, stated on the page rather than hidden: the DAT observer's
 * own per-batch counters (received / published / suppressed) exist only as log
 * lines. Nothing persists them, so this page cannot show a true accept rate —
 * only refusals and what reached storage.
 */

import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Card from '@mui/joy/Card'
import Chip from '@mui/joy/Chip'
import CircularProgress from '@mui/joy/CircularProgress'
import LinearProgress from '@mui/joy/LinearProgress'
import Modal from '@mui/joy/Modal'
import ModalClose from '@mui/joy/ModalClose'
import ModalDialog from '@mui/joy/ModalDialog'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import {
    BROKER_EMAIL_SOURCE,
    DAT_EXTENSION_SOURCE,
    EXTENSION_BOARD_SOURCES,
    EXTENSION_BOARDS_WRITER,
    NOTE_EXTRACTOR_AUDIT_KEY,
    PARADE_SOURCE,
    RATECONF_SOURCE,
    SYLECTUS_EMAIL_SOURCE,
    USPS_SOURCE,
} from '@numeo/types-core'
import * as React from 'react'
import { loadPipelineApi, type QuarantineDetail } from 'api/loadPipeline'
import SylectusActivity from './components/SylectusActivity'
import WriterActivity, { type WriterActivityLayout } from './components/WriterActivity'
import { ErrorState, Loading, Stat, StatRow, WindowPicker, useLoadPipelineData, formatNumber } from './components/shared'

/**
 * The payload behind one refusal.
 *
 * Fetched on open, never with the list: `raw` is a whole DAT posting, and
 * twenty-five of them would turn a forensic sample into a payload dump.
 *
 * A reason string is a category, not a diagnosis — "MISSING_BROKER: neither MC
 * nor company name" cannot tell you whether DAT sent a field we do not read,
 * sent it empty, or sent it somewhere we did not look. This is where that gets
 * answered.
 */
function PayloadDialog({ id, source, onClose }: { id: string; source?: string; onClose: () => void }) {
    const [detail, setDetail] = React.useState<QuarantineDetail | null>(null)
    const [error, setError] = React.useState<unknown>(null)

    React.useEffect(() => {
        let cancelled = false
        setDetail(null)
        setError(null)
        loadPipelineApi
            .getQuarantineDetail(id, source)
            .then((d) => !cancelled && setDetail(d))
            .catch((err) => !cancelled && setError(err))
        return () => {
            cancelled = true
        }
    }, [id, source])

    return (
        <Modal open onClose={onClose}>
            <ModalDialog sx={{ width: 'min(900px, 92vw)', maxHeight: '88vh', overflow: 'auto' }}>
                <ModalClose />
                <Typography level="title-lg">Refused payload</Typography>

                {error ? <ErrorState error={error} /> : null}
                {!detail && !error ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : null}

                {detail ? (
                    <>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 0.5, columnGap: 2, mb: 2 }}>
                            <Typography level="body-xs" textColor="text.tertiary">
                                Reason
                            </Typography>
                            <Typography level="body-sm">
                                {detail.reason ?? '—'} — {detail.detail ?? '—'}
                            </Typography>

                            <Typography level="body-xs" textColor="text.tertiary">
                                Batch
                            </Typography>
                            {/* The join key back to the endpoint request and everything it logged. */}
                            <Typography level="body-xs" sx={{ fontFamily: 'monospace' }}>
                                {detail.batchId ?? '—'}
                            </Typography>

                            <Typography level="body-xs" textColor="text.tertiary">
                                Posting
                            </Typography>
                            <Typography level="body-xs" sx={{ fontFamily: 'monospace' }}>
                                {detail.postingId ?? '—'}
                            </Typography>

                            <Typography level="body-xs" textColor="text.tertiary">
                                Received → refused
                            </Typography>
                            <Typography level="body-xs">
                                {detail.receivedAt ? new Date(detail.receivedAt).toLocaleString() : '—'} →{' '}
                                {detail.quarantinedAt ? new Date(detail.quarantinedAt).toLocaleString() : '—'}
                            </Typography>
                        </Box>

                        {detail.rawOmitted ? (
                            <Alert color="warning">
                                Source row omitted ({detail.rawBytes} bytes). SHA-256: {detail.rawSha256}
                            </Alert>
                        ) : null}
                        {detail.rawTruncated ? (
                            <Alert color="warning" variant="soft" sx={{ mb: 1 }}>
                                Payload truncated — this is the beginning of it, not the whole thing.
                            </Alert>
                        ) : null}

                        {detail.raw === null && !detail.rawOmitted ? (
                            <Typography level="body-sm">
                                No payload stored. Either the refusal happened early enough that there was nothing readable to keep, or this writer keeps no
                                payload by design (Sylectus, Parade and USPS note the reason and a few fields only).
                            </Typography>
                        ) : detail.raw !== null ? (
                            <Box
                                component="pre"
                                sx={{ fontSize: 11, m: 0, p: 1.5, borderRadius: 'sm', bgcolor: 'background.level1', overflow: 'auto', maxHeight: '52vh' }}
                            >
                                {detail.raw}
                            </Box>
                        ) : null}
                    </>
                ) : null}
            </ModalDialog>
        </Modal>
    )
}

/** A partner writer keeps the reason, a few fields and the raw posting of a refusal, for 21 days (ADR-035.1). */
const PARTNER_NOTE = ' A refusal keeps the reason, a few fields and the posting as the source sent it, and expires after 21 days.'

/** What each writer's own counters are called on its page. The counter names are the writer's (`writer-hourly.ts`). */
const DAT_ACTIVITY: WriterActivityLayout = {
    lastEventLabel: 'Last load',
    rows: [
        [
            { counter: 'received', label: 'Received', hint: 'loads the extensions sent' },
            { counter: 'published', label: 'Published', hint: 'a SEEN load update on the load topic', tone: 'success' },
            { counter: 'closed', label: 'Closed', hint: 'an UPDATED status (covered, inferred) from a DAT cancel' },
            { counter: 'detailsPublished', label: 'Details', hint: 'an UPDATED from an opened page: commodity and stop notes' },
            { counter: 'suppressed', label: 'Duplicates', hint: 'seen already, culled before parsing' },
            { counter: 'refused', label: 'Refused', hint: 'quarantined with a reason', tone: 'warning' },
        ],
        [
            { counter: 'privateNetwork', label: 'Private network', hint: "a broker's private DAT listing: not published, not stored" },
            {
                counter: 'redactedPoster',
                label: 'Poster withheld',
                hint: 'DAT sent the listing without its poster (isRedacted) and the writer has no load to update: not stored',
            },
            {
                counter: 'redactedUpdated',
                label: 'Updated without poster',
                hint: 'an update DAT sent without its poster, applied to the load already published (still available, rate, expiry)',
            },
            { counter: 'unknownPosting', label: 'Unknown cancel', hint: 'a cancel for a posting the writer does not remember: nothing to name' },
        ],
    ],
    quietNote: 'No DAT load in this window. The extensions send loads while dispatchers search; if they should be, check loads-ingest.',
}

/** Parade and USPS share a writer; the rows differ only in what a poll finds. */
const partnerActivity = (changes: { counter: string; label: string; hint: string }[]): WriterActivityLayout => ({
    lastEventLabel: 'Last poll',
    rows: [
        [
            { counter: 'published', label: 'Published', hint: 'a SEEN load update on the load topic', tone: 'success' },
            { counter: 'closed', label: 'Closed', hint: 'an UPDATED status: gone from the feed (covered, inferred) or expired' },
            { counter: 'refused', label: 'Refused', hint: 'quarantined with a reason', tone: 'warning' },
            { counter: 'failed', label: 'Failed', hint: 'geocoding or the topic was down; retried next poll', tone: 'danger' },
            { counter: 'closeRefused', label: 'Close refused', hint: 'rejected by the contract; quarantined', tone: 'warning' },
            { counter: 'closeFailed', label: 'Close failed', hint: 'retried next poll', tone: 'danger' },
        ],
        [
            { counter: 'polls', label: 'Polls', hint: 'completed polls' },
            { counter: 'pollsFailed', label: 'Polls failed', hint: 'the feed or the database failed', tone: 'danger' },
            { counter: 'received', label: 'Loads read', hint: 'every load on the feed, every poll' },
            ...changes,
        ],
    ],
    quietNote: 'No poll in this window. Check that search-scheduler has the feed turned on and that loads-ingest has its credentials.',
    showPlacesAndLag: false,
})

const PARADE_ACTIVITY = partnerActivity([
    { counter: 'changesNew', label: 'New', hint: 'not on the feed at the last poll' },
    { counter: 'changesUpdated', label: 'Updated', hint: 'posting_updated_at moved' },
    { counter: 'changesVanished', label: 'Vanished', hint: 'gone from a complete feed' },
])

const USPS_ACTIVITY = partnerActivity([
    { counter: 'changesListed', label: 'New or changed', hint: 'new, changed, or back after a close' },
    { counter: 'changesExpired', label: 'Expired', hint: 'auction ended' },
    { counter: 'changesVanished', label: 'Vanished', hint: 'gone from the board' },
])

/** The rate con writer (ADR-036): the funnel, how each identity was obtained, and the facts the read missed (`likely_missed` is the actionable verdict). */
const RATECON_ACTIVITY: WriterActivityLayout = {
    lastEventLabel: 'Last rate con',
    rows: [
        [
            { counter: 'published', label: 'Published', hint: 'a BOOKED load update on the load topic', tone: 'success' },
            { counter: 'refused', label: 'Refused', hint: 'quarantined with a reason', tone: 'warning' },
            { counter: 'unavailable', label: 'Unavailable', hint: 'geo or the topic was down; replay these job ids', tone: 'danger' },
            { counter: 'notALoad', label: 'Not a load', hint: 'no load number, or not a rate confirmation' },
        ],
        [
            { counter: 'identityComputed', label: 'Identity computed', hint: "loadHash from the rate con's own facts" },
            { counter: 'identityMatched_REFERENCE', label: 'Matched by reference', hint: 'a listing carries the same reference number' },
            { counter: 'identityMatched_THREAD', label: 'Matched by thread', hint: 'the load our own offer on this email thread was about' },
            { counter: 'identityMatched_LANE_AND_DATE', label: 'Matched by lane and date', hint: 'the one load on the same lane, pickup day and broker' },
            { counter: 'identityLookupFailed', label: 'Lookup failed', hint: 'search was unreachable; the identity was computed instead', tone: 'warning' },
        ],
        [
            { counter: 'gap_weight_likely_missed', label: 'Weight missed', hint: 'on the page, not read', tone: 'warning' },
            { counter: 'gap_commodity_likely_missed', label: 'Commodity missed', hint: 'on the page, not read', tone: 'warning' },
            { counter: 'gap_brokerMc_likely_missed', label: 'Broker MC missed', hint: 'on the page, not read', tone: 'warning' },
            { counter: 'gap_issueDate_unknown', label: 'No issue date', hint: 'the coverage check declares none' },
            { counter: 'gap_components_unusable', label: 'Components off', hint: 'line-haul and fees do not add up to the total', tone: 'warning' },
            { counter: 'gap_place_unusable', label: 'Place by point', hint: 'a stop found only by its point, not its name or ZIP', tone: 'warning' },
        ],
    ],
    quietNote:
        'No rate con in this window. Rate cons reach the writer from the document pipeline, for tenants whose ratecon-document-pipeline flag is shadow or on.',
}

/**
 * The broker reply writer (ADR-037): one outcome per reply on a thread we sent about a load, and what a published one
 * said. The first row is the send side (ADR-037.1): which first emails kept a record a reply can be read against.
 */
const BROKER_REPLY_ACTIVITY: WriterActivityLayout = {
    lastEventLabel: 'Last email',
    rows: [
        [
            { counter: 'sendRecorded', label: 'Emails recorded', hint: "first emails to the listing's broker: a reply will be read", tone: 'success' },
            { counter: 'sendNoListing', label: 'No listing', hint: 'search did not know the load' },
            { counter: 'sendNoBroker', label: 'No broker contact', hint: 'the listing has no broker email to compare with', tone: 'warning' },
            { counter: 'sendNotBroker', label: 'Not to the broker', hint: "sent to someone other than the listing's broker", tone: 'warning' },
            { counter: 'sendLookupFailed', label: 'Lookup failed', hint: 'search was down or slow; sent without a record', tone: 'danger' },
        ],
        [
            { counter: 'handled', label: 'Replies', hint: 'inbound messages read while the flag was on' },
            { counter: 'published', label: 'Published', hint: 'an UPDATED load update on the load topic', tone: 'success' },
            {
                counter: 'refused',
                label: 'Refused',
                hint: 'another pickup day, stops it could not check, or the contract refused it; see the notes',
                tone: 'warning',
            },
            { counter: 'unavailable', label: 'Unavailable', hint: 'the processor, geo or the topic was down; not retried', tone: 'danger' },
        ],
        [
            { counter: 'closed', label: 'Closed', hint: 'covered or cancelled' },
            { counter: 'priced', label: 'Priced', hint: "the broker's quote, flat or per mile" },
            { counter: 'stopsPublished', label: 'Stops', hint: "the reply's stops matched the load" },
        ],
        [
            { counter: 'noFacts', label: 'Nothing to publish', hint: '"let me check", or only parts that were left out' },
            { counter: 'noRecord', label: 'No record', hint: 'not a thread we sent about a load, or past its 5 hours' },
            { counter: 'notBroker', label: 'Not the broker', hint: "from someone other than the listing's broker we emailed: ignored" },
            { counter: 'notReply', label: 'Not a reply', hint: 'our own send, or a new message' },
        ],
    ],
    quietNote: 'No email in this window. Emails are recorded and replies read only while broker-reply-enabled is on, and only on threads begun after it was.',
}

/**
 * The extension boards share one writer (ADR-035.4). Each input has one terminal outcome after queued work finishes. The
 * writer publishes no closes.
 */
const BOARDS_ACTIVITY: WriterActivityLayout = {
    lastEventLabel: 'Last load',
    rows: [
        [
            { counter: 'received', label: 'Received', hint: 'loads the extensions sent from enabled boards' },
            { counter: 'published', label: 'Published', hint: 'SEEN updates on the load topic', tone: 'success' },
            { counter: 'suppressed', label: 'Suppressed', hint: 'identical to a listing already published in the last 5 hours' },
            { counter: 'ignored', label: 'Ignored', hint: 'explicit non-load, inactive or non-public records' },
            { counter: 'refused', label: 'Refused', hint: 'quarantined with a reason', tone: 'warning' },
            {
                counter: 'failed',
                label: 'Failed',
                hint: 'geocoding or the topic was down, the instance was full, or the work hit an error or its deadline; read again at the next sighting',
                tone: 'danger',
            },
        ],
        [
            {
                counter: 'noteFailed',
                label: 'Notes not stored',
                hint: 'the refusal or failure is counted, but Firestore rejected its note, so there is nothing to open',
                tone: 'warning',
            },
            {
                counter: 'noteSkipped',
                label: 'Notes skipped',
                hint: 'the instance had too many notes being written; the refusal or failure is counted, its note was not stored',
                tone: 'warning',
            },
            { counter: 'dependencyTimeout', label: 'Deadlines exceeded', hint: 'diagnostic count; pending writes can still succeed', tone: 'warning' },
            {
                counter: 'shutdownPending',
                label: 'Writes pending at shutdown',
                hint: 'diagnostic count; these writes had no result when draining ended',
                tone: 'warning',
            },
        ],
    ],
    quietNote: 'No load in this window. Only enabled boards reach this writer, and only while dispatchers browse them; if they should be, check loads-ingest.',
    showPlacesAndLag: false,
    boardColumns: [
        { counter: 'received', label: 'Received' },
        { counter: 'published', label: 'Published' },
        { counter: 'suppressed', label: 'Suppressed' },
        { counter: 'ignored', label: 'Ignored' },
        { counter: 'refused', label: 'Refused' },
        { counter: 'failed', label: 'Failed' },
    ],
}

/**
 * The note extractor (ADR-049): the funnel from a sighting to a published reading, what the readings said, and
 * the tripwire.
 *
 * THE LAST ROW IS THE ONE TO READ. There is no ground truth for a note — the note is the only source there is —
 * so nothing can score a reading. `gap_<CODE>_unread` counts notes that plainly mention a thing the reading
 * named in neither direction: it cannot say a reading is wrong, only that a prompt has stopped reading
 * something it used to. The refusal table below carries the notes behind each one.
 */
const NOTES_ACTIVITY: WriterActivityLayout = {
    lastEventLabel: 'Last batch',
    rows: [
        [
            { counter: 'withNote', label: 'With a note', hint: 'a sighting whose note is worth a model call' },
            { counter: 'read', label: 'Read', hint: 'a model call made; the rest were already read by this pod' },
            { counter: 'published', label: 'Published', hint: 'a reading back on the topic, under the listing it read', tone: 'success' },
            { counter: 'silent', label: 'States nothing', hint: 'read, and the note asks for nothing — most notes' },
            { counter: 'modelFailed', label: 'Unreadable', hint: 'the model could not be reached; the next sighting tries again', tone: 'warning' },
            { counter: 'kept_REFUSED', label: 'Refused', hint: 'the contract refused the reading — a bug in the extractor', tone: 'danger' },
        ],
        [
            { counter: 'confHigh', label: 'Sure', hint: 'the model put its own confidence at 0.9 or better' },
            { counter: 'confMid', label: 'Fairly sure', hint: '0.7 to 0.9' },
            { counter: 'confLow', label: 'Unsure', hint: 'under 0.7 — shorthand it had to interpret', tone: 'warning' },
            {
                counter: 'noCodeForIt',
                label: 'No code for it',
                hint: 'a real requirement none of the codes names: the list for the next code',
                tone: 'warning',
            },
        ],
        [
            { counter: 'gap_LIFT_GATE_unread', label: 'Lift gate unread', hint: 'the note says so, the reading decided neither way', tone: 'warning' },
            { counter: 'gap_PALLET_JACK_unread', label: 'Pallet jack unread', hint: 'mentioned, decided neither way', tone: 'warning' },
            { counter: 'gap_DOCK_HIGH_unread', label: 'Dock unread', hint: 'mentioned, decided neither way', tone: 'warning' },
            { counter: 'gap_TEAM_unread', label: 'Team unread', hint: 'mentioned, decided neither way', tone: 'warning' },
            { counter: 'gap_TEMP_CONTROL_unread', label: 'Reefer unread', hint: 'mentioned, decided neither way', tone: 'warning' },
        ],
    ],
    quietNote:
        'No batch in this window. The extractor reads only Sylectus sightings that carry a note worth a model call — its subscription is filtered to them.',
}

const WRITERS = {
    dat: { source: DAT_EXTENSION_SOURCE, title: 'W-DAT Observer', note: undefined, activity: DAT_ACTIVITY },
    sylectus: {
        source: SYLECTUS_EMAIL_SOURCE,
        title: 'W-Sylectus Email',
        note: ' A Sylectus refusal keeps only the reason and a few fields, never the email, and expires after 21 days.',
        activity: undefined,
    },
    parade: { source: PARADE_SOURCE, title: 'W-Partner Parade', note: PARTNER_NOTE, activity: PARADE_ACTIVITY, refusalsOnly: true },
    usps: { source: USPS_SOURCE, title: 'W-Partner USPS', note: PARTNER_NOTE, activity: USPS_ACTIVITY, refusalsOnly: true },
    boards: {
        source: EXTENSION_BOARDS_WRITER,
        title: 'W-Observer Extension boards',
        note: ' A refusal keeps the reason, posting ID, source-row size, SHA-256 hash and the source row itself, with credential values replaced. Notes expire after 21 days. A load refused again overwrites its note, at most once an hour per instance. An exception or a timeout is noted the same way, with its stage and error. The list shows the newest 25 notes per selected board. Refusal counts come from hourly activity, including repeated refusals; the latest minute can be missing.',
        activity: BOARDS_ACTIVITY,
        refusalsOnly: true,
        byBoard: true,
    },
    ratecon: {
        source: RATECONF_SOURCE,
        title: 'W-Ratecon',
        note: " A rate con refusal keeps the reason and the route's cities and states, never the document, and expires after 21 days.",
        activity: RATECON_ACTIVITY,
    },
    brokerReply: {
        source: BROKER_EMAIL_SOURCE,
        title: 'W-Broker Reply',
        note: " A broker reply's note keeps the reason, the load and the stops' cities and states, never the email, and expires after 21 days. Left-out stops and fields (STOPS_LEFT_OUT, FIELDS_LEFT_OUT) are noted too: the rest of that reply was published, so they are not refusals. Lag is from the broker's reply to the publish.",
        activity: BROKER_REPLY_ACTIVITY,
        // Most of its notes are left-out parts, not refusals: its cards count refusals, so no sampled count here.
        refusalsOnly: true,
    },
    notes: {
        source: NOTE_EXTRACTOR_AUDIT_KEY,
        title: 'X-Note Extractor',
        note: ' The extractor is not a source: it publishes under the listing it read. What it keeps here is the note itself, and only when something went wrong or is worth a look — a refusal always, and a model failure, a tripwire hit or a requirement with no code up to fifty an hour between them. The counters above are complete either way; it is the NOTES that are capped. Everything expires after 21 days.',
        activity: NOTES_ACTIVITY,
    },
} as const

export default function LoadPipelineWriterPage({ writer }: { writer: keyof typeof WRITERS }) {
    const { source, title, note, activity } = WRITERS[writer]
    // Parade and USPS: the cards above already count refusals, so below them only the two refusal tables.
    const refusalsOnly = 'refusalsOnly' in WRITERS[writer]
    const byBoard = 'byBoard' in WRITERS[writer]
    const [board, setBoard] = React.useState('')
    const [hours, setHours] = React.useState(24)
    const [inspecting, setInspecting] = React.useState<{ id: string; source?: string } | null>(null)
    const selectedBoard = byBoard ? board || undefined : undefined
    const { data, error, loading } = useLoadPipelineData(() => loadPipelineApi.getWriter(hours, source, selectedBoard), [hours, source, selectedBoard])

    // Filtering also keeps the previous response out of view while a different board loads.
    const shown = (q: { source?: string; board?: string }): boolean => q.source === source && (!byBoard || !board || q.board === board)
    const quarantine = (data?.quarantine ?? []).filter(shown)
    const quarantineSample = (data?.quarantineSample ?? []).filter(shown)
    const refused = quarantine.reduce((sum, q) => sum + q.count, 0)
    const maxReason = Math.max(1, ...quarantine.map((q) => q.count))

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography level="h2">{title} — what was refused</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {byBoard ? (
                        <Select size="sm" value={board} onChange={(_, value) => setBoard(value ?? '')} sx={{ minWidth: 190 }}>
                            <Option value="">All boards</Option>
                            {EXTENSION_BOARD_SOURCES.map((name) => (
                                <Option key={name} value={name}>
                                    {name}
                                </Option>
                            ))}
                        </Select>
                    ) : null}
                    <WindowPicker hours={hours} onChange={setHours} maxHours={168} />
                </Box>
            </Box>
            <Typography level="body-sm" textColor="text.tertiary" sx={{ mb: 3 }}>
                Loads this writer refused, and why. A refusal is stored for forensics; it never becomes an observation.
                {note ?? null}
            </Typography>

            {writer === 'sylectus' ? <SylectusActivity hours={hours} /> : null}
            {activity ? <WriterActivity source={source} hours={hours} layout={activity} board={byBoard ? board || undefined : undefined} /> : null}

            {error ? <ErrorState error={error} /> : null}
            {loading && !data ? <Loading /> : null}

            {data?.boardCountsUnavailable ? (
                <Alert color="warning" variant="soft" sx={{ mb: 2 }}>
                    The boards&apos; hourly refusal counts could not be read, so the reason counts below are missing. They are not zero. The refusal notes are
                    still shown.
                </Alert>
            ) : null}

            {data ? (
                <>
                    {refusalsOnly ? null : (
                        <>
                            <StatRow>
                                <Stat
                                    label="Refused (sampled)"
                                    value={formatNumber(refused)}
                                    hint="capped at 500 documents"
                                    tone={refused > 0 ? 'warning' : 'neutral'}
                                />
                                <Stat label="Refusal reasons" value={formatNumber(quarantine.length)} hint="distinct causes seen" />
                            </StatRow>

                            <Alert color="neutral" variant="soft" sx={{ mb: 3 }}>
                                <Box>
                                    <Typography level="title-sm">Counts are the writer&apos;s own</Typography>
                                    <Typography level="body-sm">
                                        The numbers above are what this writer counted about itself, in its own collection, so no other source is mixed in. They
                                        are written in batches, so the latest minute can be missing. &ldquo;Refused&rdquo; in the lists below is a capped sample
                                        of this writer&apos;s quarantine store.
                                    </Typography>
                                </Box>
                            </Alert>
                        </>
                    )}

                    <Card sx={{ mb: 3 }}>
                        <Typography level="title-md" sx={{ mb: 2 }}>
                            Why loads were refused
                        </Typography>
                        {quarantine.length === 0 ? (
                            <Typography level="body-sm" textColor="text.tertiary">
                                {byBoard ? 'No refusals recorded in the hourly counters for this window.' : 'Nothing quarantined in this window.'}
                            </Typography>
                        ) : (
                            quarantine.map((q) => (
                                <Box key={`${q.board ?? q.source}|${q.board ?? ''}|${q.reason}`} sx={{ mb: 1.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography level="body-sm">
                                            <Chip size="sm" variant="outlined" sx={{ mr: 1 }}>
                                                {q.board ?? q.source}
                                            </Chip>
                                            <code>{q.reason}</code>
                                        </Typography>
                                        <Typography level="body-sm">{formatNumber(q.count)}</Typography>
                                    </Box>
                                    <LinearProgress determinate value={(q.count / maxReason) * 100} sx={{ '--LinearProgress-thickness': '6px' }} />
                                </Box>
                            ))
                        )}
                    </Card>

                    <Sheet variant="outlined" sx={{ borderRadius: 'sm', p: 2 }}>
                        <Typography level="title-md" sx={{ mb: 1 }}>
                            Recent refusals
                        </Typography>
                        <Table size="sm" stickyHeader>
                            <thead>
                                <tr>
                                    <th style={{ width: 150 }}>Writer</th>
                                    <th style={{ width: 180 }}>Reason</th>
                                    <th>Detail</th>
                                    <th style={{ width: 130 }}>Subject</th>
                                    <th style={{ width: 170 }}>When</th>
                                    <th style={{ width: 90 }} />
                                </tr>
                            </thead>
                            <tbody>
                                {quarantineSample.map((q) => (
                                    <tr key={`${q.board ?? q.source}|${q.id}`}>
                                        <td>
                                            <Typography level="body-xs">{q.board ?? q.source ?? '—'}</Typography>
                                        </td>
                                        <td>
                                            <Chip size="sm" variant="soft" color="warning">
                                                {q.reason ?? 'UNKNOWN'}
                                            </Chip>
                                        </td>
                                        <td>
                                            <Typography level="body-xs">{q.detail ?? '—'}</Typography>
                                        </td>
                                        <td>
                                            <Typography level="body-xs" sx={{ fontFamily: 'monospace' }}>
                                                {q.subject ?? q.postingId ?? '—'}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography level="body-xs">{q.quarantinedAt ? new Date(q.quarantinedAt).toLocaleString() : '—'}</Typography>
                                        </td>
                                        <td>
                                            <Button size="sm" variant="plain" onClick={() => setInspecting({ id: q.id, source: q.source })}>
                                                payload
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                                {quarantineSample.length === 0 ? (
                                    <tr>
                                        <td colSpan={6}>
                                            <Typography level="body-sm" textColor="text.tertiary">
                                                No refusals to show.
                                            </Typography>
                                        </td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </Table>
                    </Sheet>
                </>
            ) : null}

            {inspecting ? <PayloadDialog id={inspecting.id} source={inspecting.source} onClose={() => setInspecting(null)} /> : null}
        </Box>
    )
}
